import React, { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';

import { withPrefix } from 'gatsby';
import stringify from 'json-stringify-safe';
import { PatternFormat } from 'react-number-format';
import styled from 'styled-components';

import RadioOffSVG from 'assets/radio-off.inline.svg';
import RadioOnSVG from 'assets/radio-on.inline.svg';

import ContentContainer from 'components/ContentContainer/ContentContainer';
import InnerHtml from 'components/InnerHtml/InnerHtml';

import { DemoFormI } from 'interfaces/content-stack/modular-blocks/demoForm';

import { Logger } from 'services/logger';

import { encryptPassword, getEncryptionKey } from 'utils/encryption';
import { dataLayerPush } from 'utils/functions';

export const DEMOSUBMIT_ENDPOINT =
  process.env.GATSBY_DEMOSUBMIT_ENDPOINT || 'https://www.ig.com/productapplicationsgateway/demo/submit';
export const USERNAMECHECK_ENDPOINT =
  process.env.GATSBY_USERNAMECHECK_ENDPOINT || 'https://www.ig.com/productapplicationsgateway/username';
const EMAILCHECK_ENDPOINT =
  process.env.GATSBY_EMAILCHECK_ENDPOINT || 'https://www.ig.com/productapplicationsgateway/email';

export const COUNTRYLIST_ENDPOINT =
  process.env.GATSBY_COUNTRYLIST_ENDPOINT ||
  'https://www.ig.com/productapplicationsgateway/demo/configuration?countryOfApplication=us&locale=en_US';
const LOGIN_URL = process.env.GATSBY_LOGIN_URL || 'https://www.ig.com/us/tastyfx/login';
export const SUCCESS_URL = '/platforms/tastyfx-platform/demo-account-success/';
export const FAILURE_URL = '/platforms/tastyfx-platform/demo-account-failure/';

const COUNTRY = 'US';
const LOCALE = 'en_US';
const QPID = '?CHID=7&QPID=2995643023&QPPID=1';
const APP_NAME = 'webDemoAccount';

const DemoHeader = styled.h3`
  text-transform: uppercase;
  text-align: center;
  margin-bottom: 1rem;
  font-family: ${(props) => props.theme.fonts.fontFamilySecondary};
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
  font-family: ${(props) => props.theme.fonts.default};

  a {
    color: inherit;
    transition: 0.3s;
    font-weight: 500;
    text-decoration: none;

    &:hover {
      color: ${(props) => props.theme.colors.primaryRed};
    }
  }

  input[type='radio'] {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    border: 0;
    clip: rect(0, 0, 0, 0);
    overflow: hidden;
    white-space: nowrap;
  }
  input[type='radio']:focus-visible + label {
    outline: 2px solid ${(props) => props.theme.colors.primaryBlack};
    outline-offset: 4px;
  }
`;

const CheckLabel = styled.label`
  display: flex;
  flex-direction: row;
  position: relative;
  cursor: pointer;
`;

const StyledTextInput = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${(props) => props.theme.colors.gray};
  outline: none;

  &:focus {
    outline: 2px solid ${(props) => props.theme.colors.primaryBlack};
  }
`;

const StyledSelect = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${(props) => props.theme.colors.gray};
  outline: none;

  &:focus {
    border: 2px solid ${(props) => props.theme.colors.primaryBlack};
  }
`;

const StyledFieldset = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2.5rem;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    gap: 1rem;
    flex-direction: column;
  }
`;

const FormGroup = styled.div`
  width: 100%;
  position: relative;
`;

const LabelAndInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  div {
    display: none !important;
  }
`;

const StyledLabel = styled.label`
  font-weight: 600;
`;

const FailureMessage = styled.div`
  font-family: ${(props) => props.theme.fonts.default};
  color: ${(props) => props.theme.colors.ttRed};
  font-weight: 500;

  strong a {
    font-weight: 600;
  }
`;

const SubmitButton = styled.input`
  border: 0.0625rem solid ${(props) => props.theme.colors.ttRed};
  background-color: ${(props) => props.theme.colors.ttRed};
  text-decoration: none;

  color: ${(props) => props.theme.colors.white};
  font-family: ${(props) => props.theme.fonts.default};
  text-align: center;
  text-transform: uppercase;
  white-space: nowrap;
  border-radius: 5rem;
  justify-content: center;
  align-items: center;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  transition: background-color 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
  display: flex;

  &:hover {
    background-color: ${(props) => props.theme.colors.darkerRed};
    border-color: ${(props) => props.theme.colors.darkerRed};
  }

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    width: 100%;
  }
`;

const SubmitButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const SendingFormMessage = styled.p`
  margin: 0;
  font-weight: 500;
  font-size: 1.25rem;
`;

const Legend = styled.legend`
  float: unset;
  font-size: 1rem;
  font-weight: 400;
`;

const RadioOn = styled(RadioOnSVG)`
  color: ${(props) => props.theme.colors.ttRed};
`;

const RadioOff = styled(RadioOffSVG)`
  color: ${(props) => props.theme.colors.gray};
`;

export interface DemoFormProps {
  className: string;
  data: DemoFormI;
}

let eventFired = false; // Use a flag to track if the application has been started just once

function DemoForm({ data, className }: DemoFormProps) {
  const { header, usePhoneNumber, privacyNoticeAboveMainCTA, privacyNoticeBelowMainCTA } = data;

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [emailConsent, setEmailConsent] = useState(false);
  const [selectedCountry, setCountry] = useState('US');

  const [usernameValid, setUsernameValid] = useState(true);
  const [usernameAvailable, setUsernameAvailable] = useState(true);
  const [passwordValid, setPasswordValid] = useState(true);
  const [firstNameValid, setFirstNameValid] = useState(true);
  const [lastNameValid, setLastNameValid] = useState(true);
  const [emailValid, setEmailValid] = useState(true);
  const [emailDuplicateError, setEmailDuplicateError] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);

  const [phoneValid, setPhoneValid] = useState(true);
  const [countryValid, setCountryValid] = useState(true);
  const [countryList, setCountryList] = useState([] as Country[]);
  const [sendingForm, setSendingForm] = useState(false);

  const usernameRegex = /^(?=.{6,20}$)[a-z0-9]*$/im;
  const passwordRegex = /^(?=.{8,20}$)(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d\W]+$/im;
  const firstNameRegex = /^(?=.{1,50}$)[a-z]+(?:['-\s][a-z]+)*$/im;
  const lastNameRegex = /^(?=.{2,50}$)[a-z]+(?:['-\s][a-z]+)*$/im;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^[0-9]+$/;

  const validateAndCheckUsername = async () => {
    setUsernameValid(usernameRegex.test(username));
    setUsernameAvailable(await checkUsernameAvailable(username));
  };

  const validatePassword = () => {
    setPasswordValid(passwordRegex.test(password));
  };

  const validateFirstName = () => {
    setFirstNameValid(firstNameRegex.test(firstName));
  };

  const validateLastName = () => {
    setLastNameValid(lastNameRegex.test(lastName));
  };

  const validatePhone = () => {
    setPhoneValid(phoneRegex.test(phone));
  };

  const onChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;

    if (newEmail !== email) {
      setEmailDuplicateError(false);
    }

    setEmail(newEmail);
  };

  const setRedirectURL = (url: string) => {
    window.location.href = url;
  };

  const setAndValidateCountry = (targetCountry: string) => {
    const validCountry = countryList.find(
      (country: Country) => country.isoCode === targetCountry && country.redirectType === null
    );
    if (validCountry) {
      setCountryValid(true);
    } else {
      setCountryValid(false);
    }

    setCountry(targetCountry);
  };

  const redirectCountryCode = () => {
    const matchedWebsiteCountry = countryList.find((country: Country) => country.isoCode === selectedCountry);
    return matchedWebsiteCountry?.redirectWebsiteCountryCode || '';
  };

  const checkUsernameAvailable = async (username: string) => {
    const usernameApi = `${USERNAMECHECK_ENDPOINT}/${username}/check`;
    const response = await fetch(usernameApi);
    const isUsernameAvailable = await response.json();
    return isUsernameAvailable;
  };

  const checkEmailAvailable = async (email: string): Promise<boolean> => {
    try {
      const emailApi = `${EMAILCHECK_ENDPOINT}/check`;
      const response = await fetch(emailApi, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: stringify({
          emailAddress: email,
          igCompany: 'igus',
        }),
      });

      if (!response.ok) {
        Logger.error('Failed to check email availability:', response);
        return true;
      }

      const result = await response.json();

      if (typeof result.isEmailAvailable !== 'boolean') {
        return true;
      }

      return result.isEmailAvailable;
    } catch (error) {
      Logger.error('Error checking email availability:', error);
      return true;
    }
  };

  const validateAndCheckEmail = async () => {
    if (emailDuplicateError) {
      return;
    }
    const isValidFormat = emailRegex.test(email);
    setEmailValid(isValidFormat);

    if (!isValidFormat) {
      setEmailDuplicateError(false);
      return;
    }

    const isAvailable = await checkEmailAvailable(email);
    setEmailDuplicateError(!isAvailable);
  };

  const submitForm = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    validateAndCheckUsername();
    validatePassword();
    validateFirstName();
    validateLastName();
    await validateAndCheckEmail();

    if (usePhoneNumber) {
      validatePhone();
    } else {
      setPhoneValid(true);
    }

    const errors: any = {
      username: username.trim() === '' || !usernameValid || !usernameAvailable,
      password: password.trim() === '' || !passwordValid,
      firstName: firstName.trim() === '' || !firstNameValid,
      lastName: lastName.trim() === '' || !lastNameValid,
      email: email.trim() === '' || !emailValid || emailDuplicateError,
      country: !countryValid,
      phone: usePhoneNumber && (phone.trim() === '' || !phoneValid),
    };

    if (Object.values(errors).some((error) => error)) {
      const errorList: string[] = [];
      Object.keys(errors).map((err) => {
        if (errors[err]) errorList.push(err);
      });

      handleClickEvent('Validation Error', errorList);
      return;
    }

    const formData = {
      appName: APP_NAME,
      country: COUNTRY,
      countryOfApplication: selectedCountry,
      marketingOptIn: emailConsent.toString(),
      username: username,
      password: await getPasswordEncrypted(),
      passwordEncrypted: true,
      forename: firstName,
      surname: lastName,
      email: email,
      mainContactNumber: phone,
      locale: LOCALE,
      qpid: QPID,
    };

    const jsonData = stringify(formData);

    try {
      setSendingForm(true);
      const response = await fetch(DEMOSUBMIT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: jsonData,
      });

      if (!response.ok) {
        const errorData = await response.json();

        if (errorData.globalErrors?.includes('DUPLICATE_FAILURE')) {
          setEmailDuplicateError(true);
          return;
        }

        throw new Error(stringify(errorData));
      }

      handleSubmitEvent();
      setRedirectURL(withPrefix(SUCCESS_URL));
    } catch (error) {
      Logger.error('Submission failed:', error);
      setRedirectURL(withPrefix(FAILURE_URL));
    } finally {
      setSendingForm(false);
    }
  };
  interface Country {
    isoCode: string;
    localizedName: string;
    redirectWebsiteCountryCode: string | null;
    redirectType: string | null;
  }

  const PhoneTextProps = {
    id: 'phone',
    name: 'phone',
    value: phone,
    onBlur: validatePhone,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value.replace(/[\s()-]+/g, '')),
  };

  const getPasswordEncrypted = async () => {
    const { encryptionKey } = await getEncryptionKey();
    const encryptedPassword = await encryptPassword(password, encryptionKey);

    return encryptedPassword;
  };

  useEffect(() => {
    fetch(COUNTRYLIST_ENDPOINT)
      .then((response) => response.json())
      .then((json) => setCountryList(json.countries))
      .catch((error) => console.error('Error:', error));
  }, []);

  useEffect(() => {
    if (emailDuplicateError && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [emailDuplicateError]);

  const handleClickEvent = (action: string, errorList: string[] | undefined) => {
    dataLayerPush('demo_account_interaction', 'event', {
      event_category: 'Demo Account',
      event_action: action,
      event_label: undefined,
      event_value: undefined,
      field_name: errorList?.join(','),
      interaction_type: action,
      is_validation_error: errorList?.length,
    });
  };

  const handleSubmitEvent = () => {
    dataLayerPush('demo_account_signup', 'event', {
      event_category: 'Demo Account',
      event_action: 'Signup',
      event_value: undefined,
      field_name: undefined,
      demo_applications_completed: 1,
    });
  };

  const handleStartEvent = () => {
    if (eventFired) return;
    dataLayerPush('demo_account_interaction', 'event', {
      event_category: 'Demo Account',
      event_action: 'Start Application',
      event_label: undefined,
      event_value: undefined,
      field_name: undefined,
      interaction_type: 'Start Application',
      is_validation_error: 0,
    });
    eventFired = true;
  };

  function generateUsernameHint() {
    if (usernameValid && !usernameAvailable) return 'usernameHint usernameTaken';
    if (!usernameValid) return 'usernameHint invalidUsername';

    return 'usernameHint';
  }

  function generateEmailHint() {
    if (emailValid && emailDuplicateError) return 'emailHint duplicateEmail';
    if (!emailValid) return 'emailHint invalidEmailAddress';

    return 'emailHint';
  }

  return (
    <ContentContainer className={className}>
      <DemoHeader>{header}</DemoHeader>
      <StyledForm method='post' noValidate={true} action={DEMOSUBMIT_ENDPOINT} onSubmit={submitForm}>
        <StyledFieldset>
          <FormGroup>
            <LabelAndInputContainer>
              <StyledLabel htmlFor='username'>Username</StyledLabel>
              <span id='usernameHint'>Between 6 and 20 characters</span>
              <StyledTextInput
                required
                type='text'
                id='username'
                data-testid='username'
                name='username'
                aria-invalid={!usernameValid || !usernameAvailable}
                aria-describedby={generateUsernameHint()}
                value={username}
                onBlur={validateAndCheckUsername}
                onChange={(e) => {
                  setUsername(e.target.value);
                  handleStartEvent();
                }}
              />
            </LabelAndInputContainer>
            {!usernameValid && (
              <FailureMessage id='invalidUsername'>
                Please enter a valid username between 6 and 20 characters
              </FailureMessage>
            )}
            {usernameValid && !usernameAvailable && (
              <FailureMessage id='usernameTaken'>
                This username is unavailable, please choose a different one
              </FailureMessage>
            )}
          </FormGroup>
          <FormGroup>
            <LabelAndInputContainer>
              <StyledLabel htmlFor='password'>Password</StyledLabel>
              <span id='passwordHint'>At least 8 characters</span>
              <StyledTextInput
                required
                type='password'
                id='password'
                data-testid='password'
                name='password'
                aria-invalid={!passwordValid}
                aria-describedby={`passwordHint ${!passwordValid ? 'invalidPassword' : ''}`}
                value={password}
                onBlur={validatePassword}
                onChange={(e) => setPassword(e.target.value)}
              />
            </LabelAndInputContainer>
            {!passwordValid && (
              <FailureMessage id='invalidPassword'>
                Please enter a password with at least 8 characters, including 1 uppercase, 1 lowercase letter and 1
                number
              </FailureMessage>
            )}
          </FormGroup>
        </StyledFieldset>
        <StyledFieldset>
          <FormGroup>
            <LabelAndInputContainer>
              <StyledLabel htmlFor='firstName'>First name</StyledLabel>
              <StyledTextInput
                required
                type='text'
                id='firstName'
                data-testid='firstName'
                name='forename'
                aria-invalid={!firstNameValid}
                value={firstName}
                aria-describedby={!firstNameValid ? 'invalidFirstName' : undefined}
                onBlur={validateFirstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </LabelAndInputContainer>
            {!firstNameValid && <FailureMessage id='invalidFirstName'>Please enter a valid first name</FailureMessage>}
          </FormGroup>

          <FormGroup>
            <LabelAndInputContainer>
              <StyledLabel htmlFor='lastName'>Last name</StyledLabel>
              <StyledTextInput
                required
                type='text'
                id='lastName'
                data-testid='lastName'
                name='surname'
                aria-invalid={!lastNameValid}
                aria-describedby={!lastNameValid ? 'invalidLastName' : undefined}
                value={lastName}
                onBlur={validateLastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </LabelAndInputContainer>
            {!lastNameValid && <FailureMessage id='invalidLastName'>Please enter a valid last name</FailureMessage>}
          </FormGroup>
        </StyledFieldset>

        <LabelAndInputContainer>
          <StyledLabel htmlFor='email'>Email address</StyledLabel>
          <StyledTextInput
            required
            type='email'
            id='email'
            data-testid='email'
            name='email'
            aria-invalid={!emailValid || emailDuplicateError}
            aria-describedby={generateEmailHint()}
            value={email}
            ref={emailInputRef}
            onBlur={validateAndCheckEmail}
            onChange={onChangeEmail}
          />
        </LabelAndInputContainer>
        {!emailValid && <FailureMessage id='invalidEmailAddress'>Please enter a valid email address</FailureMessage>}
        {emailDuplicateError && (
          <FailureMessage id='duplicateEmail'>
            It looks like you already have an account.&nbsp;
            <strong>
              <a href={LOGIN_URL}>Click here to login</a>
            </strong>
          </FailureMessage>
        )}

        <LabelAndInputContainer>
          <StyledLabel htmlFor='countryOfResidence'>Country of residence</StyledLabel>
          <StyledSelect
            required
            value={selectedCountry}
            onChange={(e) => setAndValidateCountry(e.target.value)}
            id='countryOfResidence'
            aria-describedby={!countryValid ? 'invalidCountry' : undefined}
            aria-invalid={!countryValid}
            data-testid='countryOfResidence'
          >
            {countryList.map((country: Country, index) => {
              return (
                <option key={index} value={country.isoCode}>
                  {country.localizedName}
                </option>
              );
            })}
          </StyledSelect>
        </LabelAndInputContainer>
        {!countryValid && (
          <FailureMessage id='invalidCountry'>
            Sorry, we cannot open an account for clients with your country of residence on this website. Please visit{' '}
            <strong>
              <a href={`https://www.ig.com/${redirectCountryCode()}`} target='_blank'>
                https://www.ig.com/{redirectCountryCode()}
              </a>
            </strong>{' '}
            for more information.
          </FailureMessage>
        )}

        {usePhoneNumber && (
          <>
            <LabelAndInputContainer>
              <StyledLabel htmlFor='phone'>Phone number</StyledLabel>
              <PatternFormat
                required
                data-testid='phone'
                format='(###) ###-####'
                mask='_'
                aria-invalid={!phoneValid}
                customInput={StyledTextInput}
                aria-describedby={phoneValid ? undefined : 'invalidPhone'}
                {...PhoneTextProps}
              />
            </LabelAndInputContainer>
            {!phoneValid && <FailureMessage id='invalidPhone'>Please enter a valid phone number</FailureMessage>}
          </>
        )}
        <fieldset>
          <Legend>
            I’d like to receive information from tastyfx about trading ideas and their products and services via email.
          </Legend>

          <StyledFieldset>
            <div>
              <input
                type='radio'
                name='marketingPreferencesOptIn'
                id='yes'
                data-testid='yes'
                value={`${emailConsent}`}
                checked={emailConsent}
                onChange={(e) => setEmailConsent(!!e.target.value)}
              />
              <CheckLabel htmlFor='yes'>
                {emailConsent ? <RadioOn aria-hidden='true' /> : <RadioOff aria-hidden='true' />}&nbsp;Yes
              </CheckLabel>
            </div>

            <div>
              <input
                type='radio'
                name='marketingPreferencesOptIn'
                id='no'
                data-testid='no'
                value={`${!emailConsent}`}
                checked={!emailConsent}
                onChange={(e) => setEmailConsent(!e.target.value)}
              />
              <CheckLabel htmlFor='no'>
                {!emailConsent ? <RadioOn aria-hidden='true' /> : <RadioOff aria-hidden='true' />}&nbsp;No
              </CheckLabel>
            </div>
          </StyledFieldset>
        </fieldset>

        <div>
          <InnerHtml richText={privacyNoticeAboveMainCTA} />
        </div>

        <SubmitButtonContainer>
          <SubmitButton data-testid='submit' type='submit' value='Try our demo' />
          {sendingForm && <SendingFormMessage>Sending...</SendingFormMessage>}
        </SubmitButtonContainer>

        <div>
          <InnerHtml richText={privacyNoticeBelowMainCTA} />
        </div>
      </StyledForm>
    </ContentContainer>
  );
}

export default React.memo(DemoForm);
